import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Spinner,
  Modal,
  Form,
  Button,
} from "@themesberg/react-bootstrap";
import { getSector, postApi } from "../../../redux/api";
import toast from "react-hot-toast";
import { Typeahead } from "react-bootstrap-typeahead";
import useBankList from "../../../hooks/useBankList";

const MDA_TYPE_OPTIONS = ["normal", "self-accounting", "self-sustaining"];
const ACCOUNT_TYPE_OPTIONS = ["Savings", "Current", "Default"];

const emptyForm = {
  mda_type: "",
  account_name: "",
  bank_name: "",
  account_no: "",
  account_type: "",
};

const MdaList = () => {
  const [mdas, setMdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { bankList } = useBankList();

  const fetchMdas = () => {
    setLoading(true);
    getSector({ type: "mdas" }, "mdas", (data) => {
      setMdas(data || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchMdas();
  }, []);

  const handleEdit = (mda) => {
    setEditing(mda);
    setForm({
      mda_type: mda.mda_type || "",
      account_name: mda.account_name || "",
      bank_name: mda.bank_name || "",
      account_no: mda.account_no || "",
      account_type: mda.account_type || "",
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = () => {
    setSaving(true);
    postApi(`account_tbeams?query_type=update_mda&head=${editing.head}`, form)
      .then((res) => {
        if (res.success) {
          toast.success("MDA details updated");
          handleClose();
          fetchMdas();
        } else {
          toast.error("Update failed");
        }
      })
      .catch(() => toast.error("An error occurred"))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <Card border="light" className="shadow-sm">
        <Card.Body>
          <h5 className="mb-4">MDA List</h5>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Code</th>
                <th>Name</th>
                <th>MDA Type</th>
                <th>Account Name</th>
                <th>Bank Name</th>
                <th>Account No</th>
                <th>Account Type</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {mdas.map((mda, index) => (
                <tr key={mda.head || index}>
                  <td>{index + 1}</td>
                  <td>{mda.head}</td>
                  <td>{mda.description}</td>
                  <td>{mda.mda_type || "—"}</td>
                  <td>{mda.account_name || "—"}</td>
                  <td>{mda.bank_name || "—"}</td>
                  <td>{mda.account_no || "—"}</td>
                  <td>{mda.account_type || "—"}</td>
                  <td>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => handleEdit(mda)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Edit MDA — {editing?.head} ({editing?.description})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>MDA Type</Form.Label>
              <Form.Select
                value={form.mda_type}
                onChange={(e) =>
                  setForm({ ...form, mda_type: e.target.value })
                }
              >
                <option value="">Select MDA Type</option>
                {MDA_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Account Name</Form.Label>
              <Form.Control
                type="text"
                value={form.account_name}
                onChange={(e) =>
                  setForm({ ...form, account_name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Bank Name</Form.Label>
              <Typeahead
                id="bank-name"
                labelKey="title"
                options={bankList}
                placeholder="Select or type a bank name"
                allowNew
                selected={
                  form.bank_name
                    ? bankList.filter((b) => b.title === form.bank_name).concat(
                        !bankList.some((b) => b.title === form.bank_name)
                          ? [{ title: form.bank_name }]
                          : []
                      )
                    : []
                }
                onChange={(selected) => {
                  setForm({ ...form, bank_name: selected.length ? selected[0].title : "" });
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Account No</Form.Label>
              <Form.Control
                type="text"
                value={form.account_no}
                onChange={(e) =>
                  setForm({ ...form, account_no: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Account Type</Form.Label>
              <Form.Select
                value={form.account_type}
                onChange={(e) =>
                  setForm({ ...form, account_type: e.target.value })
                }
              >
                <option value="">Select Account Type</option>
                {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MdaList;
